 // PropTypes هنوز برای استفاده در زمان اجرا لازم است اگرچه در TS از Interfaces استفاده می‌کنیم.
import { Fragment, useEffect, useState, FC, ReactNode, ComponentType } from 'react';
import { useLocation } from 'react-router';

// material-ui
import { useTheme, styled } from '@mui/material/styles';
import {
    Box,
    ClickAwayListener,
    Divider,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Paper,
    Popper,
    Typography,
    useMediaQuery
} from '@mui/material';

import { FormattedMessage } from 'react-intl';

import NavItem from './NavItem';
import NavCollapse from './NavCollapse';
import SimpleBar from 'components/third-party/SimpleBar';
import Transitions from 'components/@extended/Transitions';
import { MenuOrientation, ThemeMode } from 'config';

import useConfig from 'hooks/useConfig';
import { handlerHorizontalActiveItem, useGetMenuMaster } from 'api/menu';

// assets
import { DownOutlined, GroupOutlined, RightOutlined } from '@ant-design/icons';


interface MenuItem {
    id: string;
    title?: string;
    type: 'item' | 'collapse' | 'group';
    icon?: ComponentType<any>;
    url?: string;
    hidden?: boolean;
    caption?: string;
    children?: MenuItem[];
    elements?: MenuItem[]; 
}

interface MenuMaster {
    isDashboardDrawerOpened: boolean;
    openedHorizontalItem: string | null;
}

interface NavGroupProps {
    item: any;
    lastItem: any;
    remItems: any;
    lastItemId: any;
    setSelectedItems: (itemId: any) => void;
    selectedItems: string | null;
    setSelectedLevel: (level: number) => void;
    selectedLevel: number;
}


const PopperStyled = styled(Popper)(({ theme }) => ({
    overflow: 'visible',
    zIndex: 1202,
    minWidth: 180,
    '&:before': {
        content: '""',
        display: 'block',
        position: 'absolute',
        top: 5,
        left: 32,
        width: 12,
        height: 12,
        transform: 'translateY(-50%) rotate(45deg)',
        zIndex: 120,
        borderWidth: '6px',
        borderStyle: 'solid',
        borderColor: `${theme.palette.background.paper}  transparent transparent ${theme.palette.background.paper}`
    }
}));

const NavGroup: FC<NavGroupProps> = ({ item, lastItem, remItems, lastItemId, setSelectedItems, selectedItems, setSelectedLevel, selectedLevel }) => {
    const theme = useTheme();
    const { pathname } = useLocation();

    const { menuOrientation } = useConfig();
    const { menuMaster } = useGetMenuMaster() as { menuMaster: MenuMaster }; 
    const drawerOpen = menuMaster.isDashboardDrawerOpened;
    const selectedID = menuMaster.openedHorizontalItem;

    const downLG = useMediaQuery(theme.breakpoints.down('lg'));

    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [currentItem, setCurrentItem] = useState<MenuItem>(item);

    const openMini = Boolean(anchorEl);

    useEffect(() => {
        if (lastItem) {
            if (item.id === lastItemId) {
                const localItem: MenuItem = { ...item };
                const elements = remItems.map((ele:any) => ele.elements);
                localItem.children = elements.flat(1);
                setCurrentItem(localItem);
            } else {
                setCurrentItem(item);
            }
        }
    }, [item, lastItem, downLG]);

    const checkOpenForParent = (child: MenuItem[], id: string) => {
        child.forEach((ele) => {
            if (ele.children?.length) {
                checkOpenForParent(ele.children, currentItem.id);
            }
            if (ele.url === pathname) {
                handlerHorizontalActiveItem(id);
            }
        });
    };

    const checkSelectedOnload = (data: MenuItem) => {
        const childrens: MenuItem[] = data.children ? data.children : [];
        childrens.forEach((itemCheck) => {
            if (itemCheck?.children?.length) {
                checkOpenForParent(itemCheck.children, currentItem.id);
            }
            if (itemCheck?.url === pathname) {
                handlerHorizontalActiveItem(currentItem.id);
            }
        });
    };

    useEffect(() => {
        checkSelectedOnload(currentItem);
        if (openMini) setAnchorEl(null);
    }, [pathname, currentItem]);

    const handleClick = (event: React.MouseEvent<HTMLDivElement> | null) => {
        if (!openMini) {
            setAnchorEl(event?.currentTarget ?? null);
        }
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const Icon = currentItem?.icon;

    const itemIcon =
      Icon && typeof Icon === 'function' ? ( 
        <Icon
          style={{
            fontSize: 20,
            stroke: '1.5',
            color: selectedID === currentItem.id ? theme.palette.primary.main : theme.palette.secondary.dark
          }}
        />
      ) : null;

    const navCollapse: ReactNode[] | undefined = item.children
        ?.filter((menuItem:any) => !menuItem?.hidden)
        ?.map((menuItem:any) => {
            switch (menuItem.type) {
                case 'collapse':
                    return (
                        <NavCollapse
                            key={menuItem.id}
                            menu={menuItem as any}
                            setSelectedItems={setSelectedItems}
                            setSelectedLevel={setSelectedLevel}
                            selectedLevel={selectedLevel}
                            selectedItems={selectedItems}
                            level={1}
                            parentId={currentItem.id}
                        />
                    );
                case 'item':
                    return <NavItem key={menuItem.id} item={menuItem as any} level={1} />;
                default:
                    return (
                        <Typography key={menuItem.id} variant="h6" color="error" align="center">
                            Fix - Group Collapse or Items
                        </Typography>
                    );
            }
        });

   const moreItems: ReactNode[] = remItems.map((itemRem:any, i:any) => (
     <Fragment key={i}>
       {itemRem.elements.length === 0 ? (
         <Typography variant="caption" sx={{ pl: 2 }}>
           {`More Items Group ${i}`}
         </Typography>
       ) : (
         itemRem.elements
           .filter((menu:any) => !menu.hidden) 
           .map((menu:any) => {
             switch (menu.type) {
               case 'collapse':
                 return (
                   <NavCollapse
                     key={menu.id}
                     menu={menu}
                     level={1}
                     parentId={currentItem.id}
                     setSelectedItems={setSelectedItems}
                     setSelectedLevel={setSelectedLevel}
                     selectedLevel={selectedLevel}
                     selectedItems={selectedItems}
                   />
                 );
               case 'item':
                 return <NavItem key={menu.id} item={menu} level={1} />;
               default:
                 return (
                   <Typography key={menu.id} variant="h6" color="error" align="center">
                     Menu Items Error
                   </Typography>
                 );
             }
           })
       )}
     </Fragment>
   ));

    const items: ReactNode[] | undefined = currentItem.children?.map((menu) => {
        switch (menu?.type) {
            case 'collapse':
                return (
                    <NavCollapse
                        key={menu.id}
                        menu={menu as any}
                        level={1}
                        parentId={currentItem.id}
                        setSelectedItems={setSelectedItems}
                        setSelectedLevel={setSelectedLevel}
                        selectedLevel={selectedLevel}
                        selectedItems={selectedItems}
                    />
                );
            case 'item':
                return <NavItem key={menu.id} item={menu as any} level={1} />; 
            default:
                return (
                    <Typography key={menu?.id} variant="h6" color="error" align="center">
                        Menu Items Error
                    </Typography>
                );
        }
    });

    const popperId = openMini ? `group-pop-${item.id}` : undefined;

    return (
        <>
            {menuOrientation === MenuOrientation.VERTICAL || downLG ? (
                <List
                    subheader={
                        <>
                            {item.title ? (
                                drawerOpen && (
                                    <Box sx={{ pl: 3, mb: 1.5 }}>
                                        <Typography variant="subtitle2" color={theme.palette.mode === ThemeMode.DARK ? 'textSecondary' : 'text.secondary'}>
                                            {item.title}
                                        </Typography>
                                        {item.caption && (
                                            <Typography variant="caption" color="secondary">
                                                {item.caption}
                                            </Typography>
                                        )}
                                    </Box>
                                )
                            ) : (
                                <Divider sx={{ my: 0.5 }} />
                            )}
                        </>
                    }
                    sx={{ mt: drawerOpen && item.title ? 1.5 : 0, py: 0, zIndex: 0 }}
                >
                    {navCollapse}
                </List>
            ) : (
                <List>
                    <ListItemButton
                        selected={selectedID === currentItem.id}
                        sx={{
                            p: 1,
                            my: 0.5,
                            mr: 1,
                            display: 'flex',
                            alignItems: 'center',
                            backgroundColor: 'inherit',
                            '&.Mui-selected': {
                                bgcolor: 'transparent'
                            }
                        }}
                        onMouseEnter={handleClick}
                        onClick={handleClick}
                        onMouseLeave={handleClose}
                        aria-describedby={popperId}
                    >
                        {itemIcon && (
                            <ListItemIcon sx={{ minWidth: 28 }}>
                                {currentItem.id === lastItemId ? <GroupOutlined style={{ fontSize: 20, stroke: '1.5' }} /> : itemIcon}
                            </ListItemIcon>
                        )}
                        <ListItemText
                            sx={{ mr: 1 }}
                            primary={
                                <Typography
                                    variant="body1"
                                    color={selectedID === currentItem.id ? theme.palette.primary.main : theme.palette.secondary.dark}
                                >
                                    {currentItem.id === lastItemId ? <FormattedMessage id="more-items" /> : currentItem.title}
                                </Typography>
                            }
                        />
                        {openMini ? (
                            <DownOutlined style={{ fontSize: 16, stroke: '1.5' }} />
                        ) : (
                            <RightOutlined style={{ fontSize: 16, stroke: '1.5' }} />
                        )}
                        {anchorEl && (
                            <PopperStyled
                                id={popperId}
                                open={openMini}
                                anchorEl={anchorEl}
                                placement="bottom-start"
                                style={{
                                    zIndex: 2001
                                }}
                            >
                                {({ TransitionProps }) => (
                                    <Transitions in={openMini} {...TransitionProps}>
                                        <Paper
                                            sx={{
                                                mt: 0.5,
                                                py: 1.25,
                                                boxShadow: theme.shadows[8],
                                                backgroundImage: 'none'
                                            }}
                                        >
                                            <ClickAwayListener onClickAway={handleClose}>
                                                <>
                                                    <SimpleBar
                                                        sx={{
                                                            minWidth: 200,
                                                            overflowX: 'hidden',
                                                            overflowY: 'auto',
                                                            maxHeight: 'calc(100vh - 170px)'
                                                        }}
                                                    >
                                                        {currentItem.id !== lastItemId ? items : moreItems}
                                                    </SimpleBar>
                                                </>
                                            </ClickAwayListener>
                                        </Paper>
                                    </Transitions>
                                )}
                            </PopperStyled>
                        )}
                    </ListItemButton>
                </List>
            )}
        </>
    );
};

export default NavGroup;